import { useState, type ReactNode } from "react";
import { Link, NavLink, useLocation } from "react-router";
import { useI18n } from "@wato/i18n/react";
import { cx, Icon, LockIcon } from "@wato/ui";
import { NavIcon } from "./nav-icons";
import type { NavItem } from "./nav-config";
import { sidebarSections, topItemForPath } from "./nav-config";

const MORE_KEY = "wato.sidebar.moreOpen";

function readMoreOpen(): boolean {
  try {
    return window.localStorage.getItem(MORE_KEY) === "1";
  } catch {
    return false;
  }
}

function writeMoreOpen(open: boolean): void {
  try {
    window.localStorage.setItem(MORE_KEY, open ? "1" : "0");
  } catch {
    /* storage unavailable: the choice lasts until reload */
  }
}

const ROW =
  "flex h-11 items-center gap-3 rounded-xl px-3 text-sm focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-app-rail-fg";
/** OWN-219: icon-rail tile, 48px square, centred. */
const RAIL_TILE = "size-12 justify-center px-0";
const linkClass = ({ isActive }: { isActive: boolean }) =>
  isActive
    ? "bg-app-rail-active font-semibold text-app-on-rail-active"
    : "text-app-rail-muted hover:bg-app-rail-fg/10 hover:text-app-rail-fg";

function matches(item: NavItem, rel: string): boolean {
  if (item.to === "") return rel === "";
  return item.end ? rel === item.to : rel === item.to || rel.startsWith(`${item.to}/`);
}

/**
 * FB3-22: exactly the top items (max 8). The section a route belongs to stays highlighted; the
 * section's other pages are tabs above the page content (`SectionTabs`).
 */
export function SidebarNav({
  workspaceId,
  top,
  simplified = false,
  collapsed = false,
  onNavigate,
}: {
  workspaceId: string;
  top: readonly NavItem[];
  /** FB2-50: inbox/contacts-only member; shows a one-line note about other areas. */
  simplified?: boolean;
  /** Desktop icon rail (64px): labels become tooltips and accessible names only. */
  collapsed?: boolean;
  onNavigate?: () => void;
}) {
  const { t } = useI18n();
  const { pathname } = useLocation();
  const base = `/w/${workspaceId}`;
  const rel = pathname.startsWith(base) ? pathname.slice(base.length).replace(/^\/|\/$/g, "") : "";
  const bestMatch = () =>
    [...top]
      .filter((i) => !i.locked && matches(i, rel))
      .sort((a, b) => b.to.length - a.to.length)[0]?.to;
  // b106: the core sidebar lists section pages (templates, flows, catalog) directly, so a page that
  // is not one of the listed top items is highlighted by its own route instead of its section.
  const sectionTo = topItemForPath(rel)?.to;
  const activeTo = simplified || !top.some((i) => i.to === sectionTo) ? bestMatch() : sectionTo;
  const href = (item: Pick<NavItem, "to">) => (item.to === "" ? base : `${base}/${item.to}`);
  const cls = (item: NavItem) => () =>
    cx(ROW, collapsed && RAIL_TILE, linkClass({ isActive: item.to === activeTo }));
  const [moreOpen, setMoreOpen] = useState(readMoreOpen);
  const label = (key: NavItem["label"]) => (
    <span className={collapsed ? "sr-only" : "min-w-0 flex-1 truncate"}>{t(key)}</span>
  );

  const renderItem = (item: NavItem): ReactNode =>
    item.locked ? (
      <li key={item.to}>
        <Link
          to={href({ to: item.locked.to })}
          className={cx(
            ROW,
            collapsed && RAIL_TILE,
            "text-app-rail-muted hover:bg-app-rail-fg/10 hover:text-app-rail-fg",
          )}
          data-locked="true"
          title={`${t(item.label)} - ${t(item.locked.reasonKey)}`}
          onClick={onNavigate}
        >
          <LockIcon className="size-5 shrink-0" />
          {label(item.label)}
          <span className="sr-only">{t(item.locked.reasonKey)}</span>
        </Link>
      </li>
    ) : (
      <li key={item.to}>
        <NavLink
          to={href(item)}
          end={item.end ?? false}
          className={cls(item)}
          title={collapsed ? t(item.label) : undefined}
          onClick={onNavigate}
        >
          <NavIcon to={item.to} />
          {label(item.label)}
        </NavLink>
      </li>
    );

  // The icon rail and the inbox-only nav stay one flat list; the full sidebar gets sections.
  if (collapsed || simplified) {
    return (
      <ul className={cx("flex flex-col gap-1", collapsed && "items-center")}>
        {top.map(renderItem)}
        {simplified ? (
          <li>
            <p
              className={cx("px-3 py-2 text-xs text-app-rail-muted", collapsed && "sr-only")}
              data-testid="nav-role-note"
            >
              {t("dashboard.nav.roleNote")}
            </p>
          </li>
        ) : null}
      </ul>
    );
  }

  const sections = sidebarSections(top);
  return (
    <div className="flex flex-col gap-3">
      {sections.map((section) => {
        const headingId = `nav-section-${section.id}`;
        const holdsActive = section.items.some((i) => i.to === activeTo);
        const open = !section.collapsible || moreOpen || holdsActive;
        return (
          <div
            key={section.id}
            data-testid={`nav-section-${section.id}`}
            className="flex flex-col gap-1"
          >
            {section.collapsible ? (
              <button
                type="button"
                id={headingId}
                aria-expanded={open}
                aria-controls={`${headingId}-items`}
                disabled={holdsActive}
                title={t(
                  open ? "dashboard.nav.section.moreHide" : "dashboard.nav.section.moreShow",
                )}
                onClick={() => {
                  writeMoreOpen(!moreOpen);
                  setMoreOpen(!moreOpen);
                }}
                className="flex min-h-9 items-center gap-2 rounded-md px-3 text-start text-xs font-semibold text-app-rail-muted hover:text-app-rail-fg focus-visible:outline-2 focus-visible:outline-app-rail-fg disabled:cursor-default"
              >
                <span className="flex-1">{t(section.label)}</span>
                {holdsActive ? null : (
                  <Icon name={open ? "chevron-up" : "chevron-down"} size={14} />
                )}
              </button>
            ) : (
              <p id={headingId} className="px-3 pt-1 text-xs font-semibold text-app-rail-muted">
                {t(section.label)}
              </p>
            )}
            <ul
              id={`${headingId}-items`}
              aria-labelledby={headingId}
              hidden={!open}
              className="flex flex-col gap-1"
            >
              {section.items.map(renderItem)}
            </ul>
          </div>
        );
      })}
    </div>
  );
}
